// src/app/services/patient.service.ts

import { Injectable } from '@angular/core';
import { Patient } from '../interfaces/patient.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiServiceService } from './api-service.service';
import { LocalStorageUtil } from '../util/local-storage-util';
import { LoginService } from './login.service';
import { DefaultResponseDTO } from '../dto/default/default-response.dto';
import { PatientByNameDTO } from '../dto/patient-by-name.dto';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  constructor(private apiService: ApiServiceService) {}

  public savePatient(patient: Patient): Observable<Patient> {
    const signature = LocalStorageUtil.getSignature();
    patient.planName =
      signature?.planName && signature?.planName !== ''
        ? signature.planName
        : 'Plano Básico';

    if (patient.id) {
      return this.apiService.put(`/patient/update`, patient);
    }
    return this.apiService.post(`/patient/insert`, patient);
  }

  public getPatientList(
    params?: PatientServiceGetListParam
  ): Observable<{ list: Array<Patient>; total: number }> {
    let url: string = `/patient/list`;
    const paramString = this.transformParamsIntoString(params);
    if (paramString) url += paramString;
    return this.apiService.get(url);
  }

  public activatePatient(id: number): Observable<Patient> {
    return this.apiService.patch(`/patient/${id}/activate`, {});
  }

  public deactivatePatient(id: number): Observable<Patient> {
    return this.apiService.patch(`/patient/${id}/deactivate`, {});
  }

  public getPatientById(id?: number): Observable<Patient> {
    const url: string = `/patient/id/${id}`;
    return this.apiService.get(url);
  }

  public patientExist(cpf: string): Observable<boolean> {
    const url: string = `/patient/exist/${cpf}`;
    return this.apiService.get(url);
  }

  /**
   * Busca paciente(s) pelo nome usando o endpoint antigo (/patient/getByName).
   * Retorna DefaultResponseDTO<PatientByNameDTO[]> para compatibilidade com código legado.
   */
  public getByNameLegacy(
    name: string,
    getAll = false
  ): Observable<DefaultResponseDTO<PatientByNameDTO[]>> {
    const url = `/patient/getByName?buscar=${name}${
      getAll ? '&getAll=true' : ''
    }`;
    return this.apiService.get(url);
  }

  public getByName(
    name: string,
    getAll?: boolean
  ): Observable<{ data: Patient[] }> {
    const params: PatientServiceGetListParam = {
      search: name,
      filters: getAll ? {} : { isDeleted: 0 },
    };
    return this.getPatientList(params).pipe(
      map((resp) => ({ data: resp.list }))
    );
  }

  private transformParamsIntoString(params: object): string {
    if (!params) return null;
    let paramString: string | null = null;

    const processObj = (
      obj: { [key: string]: any },
      parentParam: string = null
    ) => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          if (value != undefined) {
            if (Object.prototype.toString.call(value) === '[object Object]') {
              processObj(value, key);
            } else {
              let param = key;
              if (parentParam) param = `${parentParam}[${param}]`;
              if (!paramString) {
                paramString = `?${param}=${value}`;
              } else {
                paramString += `&${param}=${value}`;
              }
            }
          }
        }
      }
    };
    processObj(params);
    return paramString;
  }
}

export interface PatientServiceGetListParam {
  page?: number;
  pageSize?: number;
  search?: string;
  filters?: {
    isDeleted?: number;
  };
  order?: {
    item?: string;
    direction?: 'asc' | 'desc';
  };
}
